import type { UploadedRecord, UploadStatus } from '@/hooks/house/documents/useUploadedRecords.ts';
import { hasTagByName } from '@/utils/taskUtils';
import { RecordTag } from '@/constants/documentTags.ts';
import {
	displayRecordName,
	getClassificationTags,
	HIDDEN_CLASSIFICATION_TAG_KEYS,
	isFile,
	isImage,
	isImportant,
	isLink,
	isNote,
	isPdf,
	isUploadComplete,
} from '@/utils/documentUtils';
import { PendingDocumentShareResponse, Tag } from '@/client/types.gen';
import { isImageByNameOrMime, isPdfByNameOrMime } from '@/utils/upload/uploadUtils';

/**
 * Named document filter keys. The filter list is open-ended — classification tag
 * keys are also valid filter values — so the type keeps `string` while these
 * constants cover the known, semantic filters used in switches and UI.
 */
export const DocumentFilters = {
	Important: 'important',
	Pending: 'pending',
	Other: 'other',
} as const;

export const RecordTypeFilters = {
	Images: 'images',
	Pdfs: 'pdfs',
	Documents: 'documents',
	Notes: 'notes',
} as const;
export type RecordTypeFilter = (typeof RecordTypeFilters)[keyof typeof RecordTypeFilters];

export const RecordPrivacy = {
	Private: 'private',
	Shared: 'shared',
} as const;
export type RecordPrivacy = (typeof RecordPrivacy)[keyof typeof RecordPrivacy];

export const PrivacyFilters = {
	All: 'all',
	...RecordPrivacy,
} as const;
export type PrivacyFilterType = (typeof PrivacyFilters)[keyof typeof PrivacyFilters];

/** Rows retained across privacy changes and the lens that controls their visibility. */
export interface RecordPrivacyLens {
	retainedItems: UploadedRecord[];
	filter: PrivacyFilterType;
}

/** The privacy lens value shared by JS filtering and the retained-row CSS selector. */
export const getRecordPrivacy = (item: UploadedRecord): RecordPrivacy =>
	item.document.private ? RecordPrivacy.Private : RecordPrivacy.Shared;

/**
 * Status check functions for UploadItem
 * Provides type guards for different upload statuses
 */
export const isUploadCompleted = (item: UploadedRecord): boolean => item.status === 'completed';

export interface RecordSelectionProps {
	selectedRecordIds: Set<number>;
	onRecordSelectedChange: (documentId: number, selected: boolean) => void;
	onSelectRecords: (documentIds: number[]) => void;
	onDeselectRecords: (documentIds: number[]) => void;
	onClearSelection: () => void;
}

export const isUploadFailed = (item: UploadedRecord): boolean => item.status === 'failed';

export const isUploadInProgress = (item: UploadedRecord): boolean => item.status === 'uploading';

/** Type checks for records based on their required document payload. */
export const isFileUpload = (item: UploadedRecord): boolean => {
	return isFile(item.document);
};

export const isNoteUpload = (item: UploadedRecord): boolean => {
	return isNote(item.document);
};

export const isLinkUpload = (item: UploadedRecord): boolean => {
	return isLink(item.document);
};

export const isCriticalInfoUpload = (item: UploadedRecord): boolean => {
	return isImportant(item.document);
};

/**
 * The records that live under Important systems rather than Documents.
 *
 * Every important system is held in one record, so this set is empty or holds a single id. It is
 * what lets a readiness count describe the Documents tab: the banner's claim covers the whole
 * property, but its numbers sit above one tab and have to be answerable there.
 */
export const importantSystemsRecordIds = (records: readonly UploadedRecord[]): Set<number> =>
	new Set(records.filter(isCriticalInfoUpload).map((item) => item.document.id));

export const isPendingUpload = (item: UploadedRecord): boolean => {
	return !!item.pendingDocument;
};

export const orderPendingRecordsFirst = <T extends UploadedRecord>(items: T[]): T[] => {
	const pendingItems: T[] = [];
	const otherItems: T[] = [];

	for (const item of items) {
		if (isPendingUpload(item)) {
			pendingItems.push(item);
		} else {
			otherItems.push(item);
		}
	}

	return [...pendingItems, ...otherItems];
};

/**
 * The records URL identifies which drawer to open. Regular records key on the document's opaque
 * public_id (?id=), while a pending share carries a DocumentMinimal with no public_id, so it keys on
 * its share id under a dedicated ?pendingShareId= — keeping the two identifier namespaces separate.
 */
export interface RecordDrawerSearch {
	id?: string;
	pendingShareId?: number;
}

/** The search params that deep-link to a record's drawer. See {@link RecordDrawerSearch}. */
export const recordDrawerSearch = (item: UploadedRecord): RecordDrawerSearch =>
	isPendingUpload(item) ? { pendingShareId: item.pendingDocument?.id } : { id: item.document.public_id ?? undefined };

/** Whether a record is the one addressed by the current records search params. */
export const matchesRecordDrawerSearch = (item: UploadedRecord, search: RecordDrawerSearch): boolean => {
	if (search.pendingShareId != null) {
		return isPendingUpload(item) && item.pendingDocument?.id === search.pendingShareId;
	}
	if (search.id) {
		return item.document.public_id === search.id;
	}
	return false;
};

export const getSelectableRecordDocumentId = (item: UploadedRecord): number | null => {
	if (!isUploadCompleted(item) || isPendingUpload(item)) return null;
	return item.document.id;
};

export const getSelectableRecordDocumentIds = (items: UploadedRecord[]): number[] =>
	items.map(getSelectableRecordDocumentId).filter((documentId): documentId is number => documentId !== null);

export interface VisibleSelectionState {
	selectedVisibleCount: number;
	allVisibleSelected: boolean;
	someVisibleSelected: boolean;
}

/**
 * Summarise how the current selection covers a set of visible, selectable records.
 * Shared by the header "select all" checkbox and the mobile selection menu so both
 * derive the same all/some/none state from one place.
 */
export const getVisibleSelectionState = (
	visibleSelectableDocumentIds: number[],
	selectedRecordIds: Set<number>,
): VisibleSelectionState => {
	const selectedVisibleCount = visibleSelectableDocumentIds.filter((documentId) =>
		selectedRecordIds.has(documentId),
	).length;
	const allVisibleSelected =
		visibleSelectableDocumentIds.length > 0 && selectedVisibleCount === visibleSelectableDocumentIds.length;
	const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

	return { selectedVisibleCount, allVisibleSelected, someVisibleSelected };
};

export const hasRecordActions = (item: UploadedRecord, isUpdating = false): boolean =>
	isPendingUpload(item) || isLinkUpload(item) || isUploadFailed(item) || isUploadInProgress(item) || isUpdating;

export const getStatusText = (status: UploadStatus): string => {
	switch (status) {
		case 'uploading':
			return 'Uploading...';
		case 'failed':
			return 'Failed';
		case 'completed':
			return 'Uploaded';
		default:
			return '';
	}
};

export const getDisplayDate = (item: UploadedRecord): string => {
	if (item.document.created_at) {
		return item.document.created_at;
	}
	return '';
};

export const getDisplayName = (item: UploadedRecord): string => displayRecordName(item.document);

/**
 * Icon type for record display
 */
export type RecordIconType = 'important' | 'note' | 'image' | 'pdf' | 'file';

/**
 * Determines which icon to display for a record based on tags and file type
 * Priority order: important (critical_info tag) > note (user_note tag) > image > pdf > file
 */
export const getRecordIconType = (item: UploadedRecord): RecordIconType => {
	// Check for important/important information tag first (highest priority)
	if (isImportant(item.document)) {
		return 'important';
	}

	// Check for note tag
	if (isNote(item.document)) {
		return 'note';
	}

	// Check if it's an image file
	if (isImage(item.document)) {
		return 'image';
	}

	// Check if it's a PDF
	if (isPdf(item.document)) {
		return 'pdf';
	}

	// Fallback for documents whose tags or MIME metadata do not identify the file type.
	const filename = item.filename;
	const mimeType = '';

	if (isImageByNameOrMime(filename, mimeType)) {
		return 'image';
	}

	if (isPdfByNameOrMime(filename, mimeType)) {
		return 'pdf';
	}

	// Default to generic file icon
	return 'file';
};

/**
 * Filter functions for search and active filter
 * Used in records.tsx to filter upload items
 */
export const filterBySearchQuery = (items: UploadedRecord[], query: string): UploadedRecord[] => {
	if (!query.trim()) return items;

	const lowercaseQuery = query.toLowerCase();
	return items.filter(
		(item) =>
			item.filename.toLowerCase().includes(lowercaseQuery) ||
			item.document.description?.toLowerCase().includes(lowercaseQuery),
	);
};

/**
 * Predicate: true when the record carries at least one of the selected classification tags.
 * Use this for boolean checks; use filterByClassificationTags to filter a list.
 */
export const matchesClassificationTags = (item: UploadedRecord, selectedTagKeys: string[]): boolean => {
	if (!item.document.tags || selectedTagKeys.length === 0) return false;
	return item.document.tags.some((tag) => selectedTagKeys.includes(tag.key));
};

/**
 * Residual "Other" bucket: a finished record the classifier gave no category to (and which isn't
 * already surfaced as important or pending). These carry no chip in the list — this only makes them
 * reachable under the "Other" filter, since untagged records otherwise drop out under any filter.
 */
export const isOtherRecord = (item: UploadedRecord): boolean =>
	!isPendingUpload(item) && !isCriticalInfoUpload(item) && getClassificationTags(item.document).length === 0;

/**
 * Predicate: true when the record matches at least one of the selected special filters
 * (pending, other, with legacy support for important). Use this for boolean checks; use
 * filterBySpecialFilters to filter a list.
 */
export const matchesSpecialFilters = (item: UploadedRecord, selectedFilters: string[]): boolean => {
	return selectedFilters.some((filter) => {
		switch (filter) {
			case DocumentFilters.Important:
				// TODO: Remove this legacy filter once existing saved/URL filter state can no longer reference it.
				return isCriticalInfoUpload(item);
			case DocumentFilters.Pending:
				return isPendingUpload(item);
			case DocumentFilters.Other:
				return isOtherRecord(item);
			default:
				return false;
		}
	});
};

/** Classifies a record into its single user-facing type bucket. */
export const getRecordType = (item: UploadedRecord): RecordTypeFilter | null => {
	if (isNoteUpload(item) || isLinkUpload(item)) return RecordTypeFilters.Notes;
	if (isImage(item.document)) return RecordTypeFilters.Images;
	if (isPdf(item.document)) return RecordTypeFilters.Pdfs;
	if (isFileUpload(item)) return RecordTypeFilters.Documents;
	return null;
};

/** Whether a record belongs to one of the user-facing type buckets. */
export const matchesRecordType = (item: UploadedRecord, type: RecordTypeFilter): boolean =>
	getRecordType(item) === type;

/** Filters by selected types using OR logic. No selection means all types. */
export const filterByRecordTypes = (items: UploadedRecord[], selectedTypes: RecordTypeFilter[]): UploadedRecord[] => {
	if (selectedTypes.length === 0) return items;
	return items.filter((item) => {
		const type = getRecordType(item);
		return type !== null && selectedTypes.includes(type);
	});
};

export interface RecordTypeFilterOption {
	key: RecordTypeFilter;
	label: string;
	count: number;
}

const RECORD_TYPE_OPTIONS: ReadonlyArray<Omit<RecordTypeFilterOption, 'count'>> = [
	{ key: RecordTypeFilters.Images, label: 'Images' },
	{ key: RecordTypeFilters.Pdfs, label: 'PDFs' },
	{ key: RecordTypeFilters.Documents, label: 'Documents' },
	{ key: RecordTypeFilters.Notes, label: 'Notes' },
];

/** Builds the fixed, count-annotated list shown in the Type filter. */
export const buildRecordTypeFilterOptions = (records: UploadedRecord[]): RecordTypeFilterOption[] => {
	const counts: Record<RecordTypeFilter, number> = {
		[RecordTypeFilters.Images]: 0,
		[RecordTypeFilters.Pdfs]: 0,
		[RecordTypeFilters.Documents]: 0,
		[RecordTypeFilters.Notes]: 0,
	};

	for (const record of records) {
		const type = getRecordType(record);
		if (type !== null) counts[type]++;
	}

	return RECORD_TYPE_OPTIONS.map((option) => ({ ...option, count: counts[option.key] }));
};

/**
 * Filters upload items by multiple classification tags (OR logic)
 * Returns items that have ANY of the specified classification tags
 * If no tags are selected, returns all items
 */
export const filterByClassificationTags = (items: UploadedRecord[], selectedTagKeys: string[]): UploadedRecord[] => {
	if (selectedTagKeys.length === 0) {
		return items;
	}
	return items.filter((item) => matchesClassificationTags(item, selectedTagKeys));
};

/**
 * Filters upload items by multiple special filters (OR logic)
 * Returns items that match ANY of the selected special filters.
 * If no special filters are selected, returns all items
 */
export const filterBySpecialFilters = (items: UploadedRecord[], selectedFilters: string[]): UploadedRecord[] => {
	if (selectedFilters.length === 0) {
		return items;
	}

	return items.filter((item) => matchesSpecialFilters(item, selectedFilters));
};

/** A selectable filter (a classification tag or a special filter) annotated with its record count. */
export interface ClassificationFilterOption {
	key: string;
	label: string;
	count: number;
	type: 'tag' | 'special';
}

interface BuildClassificationFilterOptionsParams {
	records: UploadedRecord[];
	classificationTags: Tag[];
	showPendingFilter: boolean;
}

/**
 * Builds the unified, count-annotated option list shown in the tag filter: one entry per
 * classification tag plus the optional "To review" special filter, sorted by record count
 * descending. Counts span all records, not the filtered subset.
 */
export const buildClassificationFilterOptions = ({
	records,
	classificationTags,
	showPendingFilter,
}: BuildClassificationFilterOptionsParams): ClassificationFilterOption[] => {
	const tagCounts: Record<string, number> = {};
	let pendingCount = 0;
	let otherCount = 0;

	for (const item of records) {
		for (const tag of item.document.tags ?? []) {
			tagCounts[tag.key] = (tagCounts[tag.key] ?? 0) + 1;
		}
		if (isPendingUpload(item)) pendingCount++;
		if (isOtherRecord(item)) otherCount++;
	}

	const tagFilters: ClassificationFilterOption[] = classificationTags
		.filter((tag) => !HIDDEN_CLASSIFICATION_TAG_KEYS.has(tag.key))
		.map((tag) => ({
			key: tag.key,
			label: tag.name,
			count: tagCounts[tag.key] ?? 0,
			type: 'tag',
		}));

	const specialFilters: ClassificationFilterOption[] = [
		...(showPendingFilter
			? [{ key: DocumentFilters.Pending, label: 'To review', count: pendingCount, type: 'special' as const }]
			: []),
		// Residual bucket — surfaced only when there are uncategorised records to reach.
		...(otherCount > 0
			? [{ key: DocumentFilters.Other, label: 'Untagged', count: otherCount, type: 'special' as const }]
			: []),
	];

	return [...tagFilters, ...specialFilters].sort((a, b) => b.count - a.count);
};

/**
 * Filters upload items based on upload status
 * Used for filtering by completion status rather than document type
 */
export const filterByUploadStatus = (
	items: UploadedRecord[],
	status: 'completed' | 'in-progress' | 'failed' | 'all',
): UploadedRecord[] => {
	switch (status) {
		case 'completed':
			return items.filter(isUploadCompleted);
		case 'in-progress':
			return items.filter(isUploadInProgress);
		case 'failed':
			return items.filter(isUploadFailed);
		case 'all':
		default:
			return items;
	}
};

// Upload item selectors following the same pattern as documentSelectors
export const uploadSelectors = {
	filterByTag: (items: UploadedRecord[], tagName: string): UploadedRecord[] =>
		items.filter((item) => hasTagByName(item.document.tags, tagName)),

	filterWithoutTag: (items: UploadedRecord[], tagNames: RecordTag[]): UploadedRecord[] => {
		// New behavior for multiple tags
		return items.filter((item) => {
			// Item should not have any of the specified tags
			return !tagNames.some((tagName) => hasTagByName(item.document.tags, tagName));
		});
	},

	filterByStatus: (items: UploadedRecord[], status: UploadedRecord['status']): UploadedRecord[] =>
		items.filter((item) => item.status === status),

	filterInProgress: (items: UploadedRecord[]): UploadedRecord[] =>
		items.filter((item) => item.status === 'uploading'),

	filterRecent: (items: UploadedRecord[]): UploadedRecord[] => items.filter((item) => item.isRecent),

	// Generic filter function for custom predicates
	filter: <T extends UploadedRecord>(items: T[], predicate: (item: T) => boolean): T[] => items.filter(predicate),
};

/**
 * Returns fully uploaded documents (uploaded_at is set) OR documents being updated
 * Documents with active UPDATE_RECORD tasks remain visible even when uploaded_at is cleared
 */
export function getCompletedUploads(uploadItems: UploadedRecord[], activeUpdateDocIds?: Set<number>) {
	return uploadItems.filter((item) => {
		// Include if fully uploaded (uploaded_at is set)
		if (isUploadComplete(item.document)) return true;

		// Also include if document is being updated (has active UPDATE_RECORD task)
		if (activeUpdateDocIds && activeUpdateDocIds.has(item.document.id)) {
			return true;
		}

		return false;
	});
}

/**
 * Splits a list of UploadItem into status-based groups
 * Only considers documents with uploaded_at as completed
 */
export function splitUploadItems(items: UploadedRecord[]) {
	const completed = items.filter((item) => isUploadComplete(item.document));
	const inProgress = items.filter(isUploadInProgress);
	const failed = items.filter(isUploadFailed);
	const nonCompleted = items.filter((item) => !isUploadComplete(item.document));
	return { completed, inProgress, failed, nonCompleted };
}

/**
 * Converts a PendingDocumentShareResponse to UploadedRecord format
 *
 * These are treated as completed since they're shared documents,
 * not uploads in progress
 */
export function convertPendingShareToUploadedRecord(share: PendingDocumentShareResponse): UploadedRecord {
	// Create a document object from the DocumentMinimal in the share. The share carries the source
	// document's tags, summary and privacy, so surface them (the drawer/list render them directly —
	// the doc isn't in this house's pipeline, so status can't).
	const document: UploadedRecord['document'] = {
		...share.document,
		id: share.document.id,
		filename: share.document.filename,
		file_url: '', // # TODO check no share.document.file_url
		download_url: share.document.download_url || null,
		download_url_expires_at: share.document.download_url_expires_at || null,
		created_at: share.created_at, // Use share creation date as upload date
		uploaded_at: share.created_at, // Mark as uploaded since this is a shared document (already exists)
		description: share.document.description,
		tags: share.document.tags ?? [],
		summary: share.document.summary ?? null,
		private: share.document.private ?? true,
		house_id: share.house_id,
		uploaded_by_id: share.shared_by_user_id,
	};

	return {
		id: `pending-${share.id}`,
		filename: share.document.filename,
		status: 'completed',
		document,
		isRecent: true,
		pendingDocument: share,
	};
}

export const PendingShareStatuses = {
	Pending: 'pending',
	Accepted: 'accepted',
	Rejected: 'rejected',
	Expired: 'expired',
	Cancelled: 'cancelled',
} as const;

export const PendingShareTargets = {
	Review: 'review',
	Document: 'document',
	Unavailable: 'unavailable',
} as const;

export type PendingShareTarget =
	| { kind: typeof PendingShareTargets.Review }
	| { kind: typeof PendingShareTargets.Document; documentPublicId: string }
	| { kind: typeof PendingShareTargets.Unavailable };

/**
 * Resolves what a `?pendingShareId=` deep link should open. Notification and email links keep the
 * share-id URL shape even after the share is actioned, so the records page resolves it at the
 * destination: a still-pending share opens its review drawer, an accepted share redirects to the
 * record its acceptance cloned (linked back via source_document_id), and anything else
 * (rejected/expired/deleted/unknown) has nothing left to open.
 */
export const resolvePendingShareTarget = (
	pendingShareId: number,
	shares: PendingDocumentShareResponse[],
	findMaterializedDocument: (sourceDocumentId: number) => { public_id?: string | null } | undefined,
): PendingShareTarget => {
	const share = shares.find((s) => s.id === pendingShareId);
	if (share?.status === PendingShareStatuses.Pending) return { kind: PendingShareTargets.Review };

	const materializedPublicId = share ? findMaterializedDocument(share.document_id)?.public_id : undefined;
	return materializedPublicId
		? { kind: PendingShareTargets.Document, documentPublicId: materializedPublicId }
		: { kind: PendingShareTargets.Unavailable };
};
